import { Link } from "react-router-dom";
import type { topics } from "@/mocks/topics";
import { formatHeat, formatDate } from "@/utils/format";

type Topic = (typeof topics)[number];

interface TopicCardProps {
  topic: Topic;
  rank?: number;
  layout?: "vertical" | "horizontal";
}

export default function TopicCard({ topic, rank, layout = "vertical" }: TopicCardProps) {
  if (layout === "horizontal") {
    return (
      <article className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 sm:flex-row">
        <Link
          to={`/topics/${topic.id}`}
          className="relative block h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-100 sm:h-28 sm:w-44"
        >
          <img
            src={topic.cover}
            alt={`${topic.title} 配图`}
            title={`${topic.title} - 513吃瓜热梗`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col justify-between py-1">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs text-foreground-500">
              <span className="rounded-full bg-secondary-100 px-2 py-0.5 font-label text-secondary-800">
                {topic.categoryName}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                {formatHeat(topic.heat)}
              </span>
            </div>
            <h3 className="text-base font-medium leading-snug text-foreground-950">
              <Link to={`/topics/${topic.id}`} className="hover:text-primary-700">
                {topic.title}
              </Link>
            </h3>
            <p className="mt-1.5 line-clamp-2 text-sm text-foreground-600">{topic.summary}</p>
          </div>
          <p className="mt-3 text-xs text-foreground-500">
            {topic.author} · {formatDate(topic.date)}
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300">
      <Link to={`/topics/${topic.id}`} className="relative block h-48 w-full overflow-hidden bg-background-100 md:h-52">
        <img
          src={topic.cover}
          alt={`${topic.title} 配图`}
          title={`${topic.title} - 513吃瓜热梗`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        {typeof rank === "number" ? (
          <span className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-accent-500 font-heading text-base text-foreground-950">
            {rank}
          </span>
        ) : null}
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-2.5 flex items-center gap-2 text-xs text-foreground-500">
          <span className="rounded-full bg-secondary-100 px-2 py-0.5 font-label text-secondary-800">
            {topic.categoryName}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
            {formatHeat(topic.heat)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" aria-hidden="true"></i>
            {topic.readTime} 分钟
          </span>
        </div>
        <h3 className="text-lg font-medium leading-snug text-foreground-950">
          <Link to={`/topics/${topic.id}`} className="transition-colors hover:text-primary-700">
            {topic.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">{topic.summary}</p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {topic.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded-md border border-background-200 px-2 py-0.5 text-xs text-foreground-500"
            >
              #{tag}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}